import { ChevronRight, Home } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface BreadcrumbItem {
  label: string
  href?: string
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[]
}

function prettifySegment(segment: string): string {
  return segment
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/** A URL segment as a page name in the user's language ("school-management" → "Gestion scolaire"). */
export function routeSegmentLabel(segment: string, t: (key: string, fallback: string) => string): string {
  return t(`common.${segment}`, prettifySegment(segment))
}

export function Breadcrumb({ items }: BreadcrumbProps) {
  const location = useLocation()
  const { t } = useTranslation()

  const crumbs: BreadcrumbItem[] = items ?? (() => {
    const segments = location.pathname
      .replace(/^\/app\/?/, '')
      .split('/')
      .filter(Boolean)

    let path = '/app'
    return segments.map((seg, i) => {
      path += `/${seg}`
      return {
        label: routeSegmentLabel(seg, t),
        href: i < segments.length - 1 ? path : undefined,
      }
    })
  })()

  if (crumbs.length === 0) return null

  return (
    <nav aria-label={t('common.breadcrumb', 'Breadcrumb')} className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-4">
      <Link
        to="/app/dashboard"
        className="flex items-center gap-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        <Home className="h-3.5 w-3.5" />
        <span className="sr-only">{t('common.home', 'Home')}</span>
      </Link>
      {crumbs.map((crumb, i) => (
        <span key={i} className="flex items-center gap-1.5">
          <ChevronRight className="h-3 w-3 text-gray-300 dark:text-gray-600 flex-shrink-0" />
          {crumb.href ? (
            <Link
              to={crumb.href}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors truncate max-w-[160px]"
            >
              {crumb.label}
            </Link>
          ) : (
            <span className="font-medium text-gray-700 dark:text-gray-200 truncate max-w-[200px]">
              {crumb.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  )
}
